import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import {
    PageHeader, Card, LoadingState, ErrorState, Pill,
    CopperButton, SlateButton, Modal, Field, NumberInput,
} from "@/components/common/UI";
import { VarianceBadge } from "@/components/zbc/VarianceBadge";
import AuditTrail from "@/components/common/AuditTrail";
import { useAuth, hasRole } from "@/lib/auth";
import { fmtINR, fmtDate, fmtIN } from "@/lib/format";
import {
    ArrowLeft, Edit, Copy, CheckCircle2, RefreshCw, AlertTriangle,
    ChevronDown, ChevronRight, Building, Briefcase, User, Hammer, Cog, Scissors, Flame, Calculator,
    FileSpreadsheet, TrendingUp, TrendingDown, Download, Sliders,
} from "lucide-react";
import { toast } from "sonner";

export default function ZBCDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const [z, setZ] = useState(null);
    const [customer, setCustomer] = useState(null);
    const [plant, setPlant] = useState(null);
    const [grade, setGrade] = useState(null);
    const [error, setError] = useState("");
    const [openSections, setOpenSections] = useState({ material: true, conversion: false, cutting: false, welding: false, allowances: false });
    const canEdit = hasRole(user, "CORPORATE_ADMIN", "FINANCE_PRICING");
    const [breakdownOpen, setBreakdownOpen] = useState(false);
    const [breakdown, setBreakdown] = useState(null);
    const [breakdownLoading, setBreakdownLoading] = useState(false);
    const [impactOpen, setImpactOpen] = useState(false);
    const [impact, setImpact] = useState(null);
    const [impactLoading, setImpactLoading] = useState(false);
    const [recalcSubmitting, setRecalcSubmitting] = useState(false);
    const [whatIfOpen, setWhatIfOpen] = useState(false);
    const [whatIfForm, setWhatIfForm] = useState({ rm_rate: 0, scrap_rate: 0, conversion_cost_delta_pct: 0 });
    const [whatIfResult, setWhatIfResult] = useState(null);
    const [whatIfLoading, setWhatIfLoading] = useState(false);

    const load = () =>
        api.get(`/zbcs/${id}`).then(async (r) => {
            setZ(r.data);
            const [c, p, g] = await Promise.all([
                r.data.customer_id ? api.get("/customers").then((x) => x.data.find((c) => c.id === r.data.customer_id)) : Promise.resolve(null),
                r.data.plant_id ? api.get("/plants").then((x) => x.data.find((p) => p.id === r.data.plant_id)) : Promise.resolve(null),
                r.data.steel_grade_id ? api.get("/steel-grades").then((x) => x.data.find((g) => g.id === r.data.steel_grade_id)) : Promise.resolve(null),
            ]);
            setCustomer(c); setPlant(p); setGrade(g);
        }).catch((e) => setError(formatApiError(e)));

    useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

    const verify = async () => {
        try { await api.post(`/zbcs/${id}/verify`); toast.success("ZBC verified"); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const duplicate = async () => {
        try {
            const { data } = await api.post(`/zbcs/${id}/duplicate`);
            toast.success("Duplicated");
            navigate(`/zbc/${data.id}/edit`);
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const downloadExport = async (format) => {
        try {
            const res = await api.get(`/zbcs/${id}/export.${format}`, { responseType: "blob" });
            const url = window.URL.createObjectURL(new Blob([res.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", `${z?.part_number || id}.${format}`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success(`Should-cost ${format.toUpperCase()} downloaded`);
        } catch (e) { toast.error(formatApiError(e)); }
    };

    const openBreakdown = async () => {
        setBreakdownOpen(true);
        setBreakdownLoading(true);
        try {
            const { data } = await api.get(`/zbcs/${id}/variance-breakdown`);
            setBreakdown(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setBreakdownOpen(false);
        } finally {
            setBreakdownLoading(false);
        }
    };

    // Requirement #6 (ZBC side) — steel-rate-change price impact, right at the
    // point of preparation, using the same preview/apply endpoints ZBCList's
    // bulk recalc flow already relies on.
    const openImpact = async () => {
        setImpactOpen(true);
        setImpactLoading(true);
        try {
            const { data } = await api.post(`/zbcs/${id}/recalculate-preview`);
            setImpact(data);
        } catch (e) {
            toast.error(formatApiError(e));
            setImpactOpen(false);
        } finally {
            setImpactLoading(false);
        }
    };
    const confirmRecalc = async () => {
        setRecalcSubmitting(true);
        try {
            await api.post(`/zbcs/${id}/recalculate`);
            toast.success("ZBC recalculated", { description: "Should-cost updated to current rate" });
            setImpactOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecalcSubmitting(false); }
    };
    const keepOldRate = async () => {
        setRecalcSubmitting(true);
        try {
            await api.post(`/zbcs/${id}/keep-old-rate`);
            toast.info("Kept old rate", { description: "Flag remains until you recalculate later." });
            setImpactOpen(false);
            load();
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setRecalcSubmitting(false); }
    };

    // What-If sandbox: let the user try hypothetical RM rate / scrap rate /
    // conversion cost values and see the impact on should-cost live, with
    // nothing saved. Seeded from this ZBC's own current rates so the first
    // view shows "no change" before the user starts adjusting.
    const openWhatIf = () => {
        setWhatIfForm({
            rm_rate: z.rm_rate_used || 0,
            scrap_rate: z.scrap_rate_used || 0,
            conversion_cost_delta_pct: 0,
        });
        setWhatIfResult(null);
        setWhatIfOpen(true);
    };

    useEffect(() => {
        if (!whatIfOpen) return;
        setWhatIfLoading(true);
        const handle = setTimeout(() => {
            api.post(`/zbcs/${id}/what-if`, {
                rm_rate: parseFloat(whatIfForm.rm_rate) || 0,
                scrap_rate: parseFloat(whatIfForm.scrap_rate) || 0,
                conversion_cost_delta_pct: parseFloat(whatIfForm.conversion_cost_delta_pct) || 0,
            })
                .then(({ data }) => setWhatIfResult(data))
                .catch((e) => toast.error(formatApiError(e)))
                .finally(() => setWhatIfLoading(false));
        }, 400);
        return () => clearTimeout(handle);
        /* eslint-disable-next-line */
    }, [whatIfOpen, whatIfForm]);

    if (error) return <ErrorState message={error} />;
    if (!z) return <LoadingState />;

    return (
        <div data-testid="zbc-detail-page">
            <div className="mb-4">
                <Link to="/zbc" className="text-[12px] text-slate-500 hover:text-[hsl(var(--copper))] inline-flex items-center gap-1">
                    <ArrowLeft className="h-3 w-3" /> Back to ZBCs
                </Link>
            </div>

            {z.recalc_pending && (
                <div className="rounded-md border border-amber-200 bg-amber-50 p-3 mb-4 flex items-start gap-2.5">
                    <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                    <div className="flex-1 text-[13px] text-amber-900">
                        <span className="font-semibold">Rate Updated · Recalculation Pending.</span> {z.recalc_reason || "A steel grade rate has changed since this ZBC was last calculated."}
                    </div>
                    {canEdit && (
                        <SlateButton variant="outline" onClick={openImpact} className="text-[12px] h-7" data-testid="zbc-view-impact-btn">
                            <RefreshCw className="h-3 w-3" /> View Price Impact
                        </SlateButton>
                    )}
                </div>
            )}

            {/* Part header */}
            <Card className="p-5 mb-4">
                <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                            <div className="font-mono text-sm font-bold text-slate-900">{z.part_number}</div>
                            <Pill tone={z.status === "VERIFIED" ? "blue" : "slate"}>
                                {z.status === "VERIFIED" ? <><CheckCircle2 className="h-2.5 w-2.5" /> Verified</> : "Draft"}
                            </Pill>
                            <Pill tone="slate">{z.rm_category.replace("_", " ")}</Pill>
                        </div>
                        <h1 className="font-display text-2xl font-semibold mb-2">{z.part_name}</h1>
                        <div className="flex flex-wrap items-center gap-4 text-[12px] text-slate-600">
                            {customer && <span className="inline-flex items-center gap-1.5"><Briefcase className="h-3 w-3 text-slate-400" />{customer.name}</span>}
                            {plant && <span className="inline-flex items-center gap-1.5"><Building className="h-3 w-3 text-slate-400" />{plant.code} · {plant.city}</span>}
                            <span className="inline-flex items-center gap-1.5"><User className="h-3 w-3 text-slate-400" />{z.created_by_name} · {fmtDate(z.created_at)}</span>
                        </div>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                        {canEdit && z.status === "DRAFT" && !z.recalc_pending && (
                            <CopperButton onClick={verify}><CheckCircle2 className="h-4 w-4" /> Mark Verified</CopperButton>
                        )}
                        {canEdit && (
                            <SlateButton onClick={() => navigate(`/zbc/${id}/edit`)} variant="outline"><Edit className="h-4 w-4" /> Edit</SlateButton>
                        )}
                        {canEdit && (
                            <SlateButton onClick={duplicate} variant="outline"><Copy className="h-4 w-4" /> Duplicate</SlateButton>
                        )}
                        <SlateButton onClick={openWhatIf} variant="outline" data-testid="zbc-whatif-btn">
                            <Sliders className="h-4 w-4" /> What-If
                        </SlateButton>
                        <SlateButton onClick={() => downloadExport("pdf")} variant="outline" data-testid="zbc-export-pdf">
                            <Download className="h-4 w-4" /> Export PDF
                        </SlateButton>
                        <SlateButton onClick={() => downloadExport("xlsx")} variant="outline" data-testid="zbc-export-xlsx">
                            <FileSpreadsheet className="h-4 w-4" /> Export Excel
                        </SlateButton>
                    </div>
                </div>
            </Card>

            {/* 4 summary cards: Should-Cost, Supplier Quote and Variance drill down
                into the weight / steel-rate / process breakdown on click. */}
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
                <SummaryCard label="SHOULD-COST" value={fmtINR(z.should_cost)} tone="copper" onClick={openBreakdown} />
                <SummaryCard label="SUPPLIER QUOTE" value={z.supplier_quote ? fmtINR(z.supplier_quote) : "—"} hint={z.supplier_name} onClick={openBreakdown} />
                <SummaryCard
                    label="VARIANCE"
                    value={z.variance_pct != null ? `${z.variance_pct > 0 ? "+" : ""}${z.variance_pct}%` : "—"}
                    badge={z.variance_badge}
                    hint={z.variance_amount != null ? `${z.variance_amount >= 0 ? "+" : ""}${fmtINR(z.variance_amount)}` : null}
                    onClick={openBreakdown}
                />
                <SummaryCard
                    label="QUOTE DATE"
                    value={z.quote_date ? fmtDate(z.quote_date) : "—"}
                    hint={z.quote_date ? quoteAge(z.quote_date) : null}
                />
            </div>

            {/* Cost breakdown accordion */}
            <Card>
                <Accordion
                    title="Material Cost"
                    icon={Hammer}
                    value={z.net_material_cost}
                    open={openSections.material}
                    onToggle={() => setOpenSections({ ...openSections, material: !openSections.material })}
                >
                    <table className="w-full text-[13px]">
                        <tbody>
                            <tr><td className="py-1 text-slate-600">Steel Grade</td><td className="text-right font-mono">{grade?.code || "—"} ({fmtINR(z.rm_rate_used || 0)}/kg)</td></tr>
                            <tr><td className="py-1 text-slate-600">Input Weight</td><td className="text-right font-mono tabular-nums">{fmtIN(z.input_weight_kg || 0, { decimals: 3 })} kg</td></tr>
                            <tr>
                                <td className="py-1 text-slate-600">Finish Weight</td>
                                <td className="text-right font-mono tabular-nums">
                                    <span className="inline-flex items-center gap-1.5">
                                        {fmtIN(z.finish_weight_kg || 0, { decimals: 3 })} kg
                                        {z.finish_weight_source && (() => {
                                            const src = String(z.finish_weight_source).toUpperCase();
                                            const conf = String(z.finish_weight_confidence || "").toUpperCase();
                                            const dot = conf === "HIGH" ? "bg-emerald-500"
                                                : conf === "MEDIUM" ? "bg-amber-500"
                                                : "bg-slate-400";
                                            const label = src === "AI_DRAWING" ? "Intelligent · Drawing"
                                                : src === "MANUAL" ? "Manual"
                                                : src === "SHAPE_RULE" ? `Rule · ${z.finish_weight_shape || "shape"} loss ${z.finish_weight_loss_pct || ""}%`
                                                : src === "SHEET_LOSS_RULE" ? "Sheet blanking loss"
                                                : src;
                                            const tip = src === "AI_DRAWING"
                                                ? "Extracted from the drawing's title-block WEIGHT note by Claude Sonnet 4.5"
                                                : src === "MANUAL"
                                                ? "Value entered manually by the user"
                                                : src === "SHAPE_RULE"
                                                ? `Auto-derived using ${z.finish_weight_shape || "shape"} machining loss of ${z.finish_weight_loss_pct}%, editable at /formula-registry`
                                                : "Auto-derived from sheet-metal blanking allowances";
                                            return (
                                                <span
                                                    className="inline-flex items-center gap-1 text-[10px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded px-1.5 py-0.5 whitespace-nowrap"
                                                    title={tip}
                                                    data-testid="zbc-detail-finish-weight-source"
                                                >
                                                    <span className={`inline-block w-1.5 h-1.5 rounded-full ${dot}`} />
                                                    {label}
                                                </span>
                                            );
                                        })()}
                                    </span>
                                </td>
                            </tr>
                            <tr><td className="py-1 text-slate-600">Scrap Weight <span className="text-[11px] text-slate-400">(Input − Finish)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-scrap-weight">{fmtIN((z.scrap_weight_kg != null ? z.scrap_weight_kg : Math.max(0, (z.input_weight_kg || 0) - (z.finish_weight_kg || 0))), { decimals: 3 })} kg</td></tr>
                            <tr><td className="py-1 text-slate-600">Scrap Rate</td><td className="text-right font-mono tabular-nums">{fmtINR(z.scrap_rate_used || 0)}/kg</td></tr>
                            <tr className="border-t border-slate-100"><td className="py-1 text-slate-600">Input RM Cost <span className="text-[11px] text-slate-400">(Rate × Input Wt)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-input-rm-cost">{fmtINR(z.input_rm_cost != null ? z.input_rm_cost : (z.gross_material_cost || 0))}</td></tr>
                            <tr><td className="py-1 text-slate-600">− Scrap Cost <span className="text-[11px] text-slate-400">(Scrap Wt × Scrap Rate)</span></td><td className="text-right font-mono tabular-nums text-emerald-600" data-testid="zbc-scrap-cost">−{fmtINR(z.scrap_cost != null ? z.scrap_cost : (z.scrap_credit || 0))}</td></tr>
                            <tr className="border-t border-slate-200"><td className="py-2 font-semibold">Net Material Cost</td><td className="text-right font-mono tabular-nums font-semibold" data-testid="zbc-net-material">{fmtINR(z.net_material_cost || 0)}</td></tr>
                        </tbody>
                    </table>
                </Accordion>

                <Accordion
                    title="Conversion Cost"
                    icon={Cog}
                    value={z.conversion_cost}
                    open={openSections.conversion}
                    onToggle={() => setOpenSections({ ...openSections, conversion: !openSections.conversion })}
                >
                    {(z.operations || []).length === 0 ? (
                        <p className="text-[12px] text-slate-500">No operations recorded.</p>
                    ) : (
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-slate-500">
                                    <th className="text-left py-1">Operation</th>
                                    <th className="text-left py-1">Machine</th>
                                    <th className="text-left py-1">UOM</th>
                                    <th className="text-right py-1">Qty/Time</th>
                                    <th className="text-right py-1">Rate</th>
                                    <th className="text-right py-1">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {z.operations.map((o, i) => (
                                    <tr key={i} className="border-b border-slate-100">
                                        <td className="py-1.5">{o.name || "—"}</td>
                                        <td className="py-1.5 font-mono">{o.machine_code || "—"}</td>
                                        <td className="py-1.5">{o.uom}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{o.qty_or_time}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(o.rate)}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums font-semibold">{fmtINR(o.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Accordion>

                <Accordion
                    title="Cutting Cost"
                    icon={Scissors}
                    value={z.cutting_cost}
                    open={openSections.cutting}
                    onToggle={() => setOpenSections({ ...openSections, cutting: !openSections.cutting })}
                >
                    {!z.cut_perimeter_mtr ? (
                        <p className="text-[12px] text-slate-500">Cutting not applicable for this part.</p>
                    ) : (
                        <table className="w-full text-[13px]">
                            <tbody>
                                <tr><td className="py-1 text-slate-600">Process</td><td className="text-right">{z.cutting_process}</td></tr>
                                <tr><td className="py-1 text-slate-600">Perimeter × Thickness</td><td className="text-right font-mono tabular-nums">{fmtIN(z.cut_perimeter_mtr, { decimals: 3 })} × {fmtIN(z.thickness_mm, { decimals: 1 })} mm</td></tr>
                                <tr>
                                    <td className="py-1 text-slate-600">Rate</td>
                                    <td className="text-right font-mono tabular-nums">
                                        {fmtINR(z.cutting_rate_used || 0)}/Mtr/mm
                                        {z.volume_tier_monthly && (
                                            <span
                                                className="ml-2 text-[10px] font-semibold text-[hsl(var(--copper))] bg-[hsl(var(--copper-soft))]/40 border border-[hsl(var(--copper))]/30 px-1.5 py-0.5 rounded"
                                                title={`Volume-tier applied @ ${z.volume_tier_monthly.toFixed(0)} pcs/month`}
                                                data-testid="cutting-volume-tier-badge"
                                            >
                                                {z.volume_tier_monthly.toFixed(0)}/mo tier
                                            </span>
                                        )}
                                    </td>
                                </tr>
                                <tr className="border-t border-slate-200"><td className="py-2 font-semibold">Cutting Cost</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(z.cutting_cost)}</td></tr>
                            </tbody>
                        </table>
                    )}
                </Accordion>

                <Accordion
                    title="Welding Cost"
                    icon={Flame}
                    value={z.welding_cost}
                    open={openSections.welding}
                    onToggle={() => setOpenSections({ ...openSections, welding: !openSections.welding })}
                >
                    {!z.has_welding || (z.weld_joints || []).length === 0 ? (
                        <p className="text-[12px] text-slate-500">No welding on this part.</p>
                    ) : (
                        <table className="w-full text-[12px]">
                            <thead>
                                <tr className="border-b border-slate-200 text-slate-500">
                                    <th className="text-left py-1">Joint</th>
                                    <th className="text-right py-1">Length</th>
                                    <th className="text-right py-1">Fillet (used ↑)</th>
                                    <th className="text-right py-1">Rate</th>
                                    <th className="text-right py-1">Qty</th>
                                    <th className="text-right py-1">Cost</th>
                                </tr>
                            </thead>
                            <tbody>
                                {z.weld_joints.map((j, i) => (
                                    <tr key={i} className="border-b border-slate-100">
                                        <td className="py-1.5">{j.description || "—"}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtIN(j.length_mtr, { decimals: 2 })} mtr</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{j.fillet_mm}mm → <span className="text-[hsl(var(--copper))]">{j.fillet_used_mm}mm</span></td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{fmtINR(j.rate)}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums">{j.qty}</td>
                                        <td className="py-1.5 text-right font-mono tabular-nums font-semibold">{fmtINR(j.cost)}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </Accordion>

                <Accordion
                    title="Allowances (ABC COMPANY Standard Template)"
                    icon={Calculator}
                    value={z.allowance_total}
                    open={openSections.allowances}
                    onToggle={() => setOpenSections({ ...openSections, allowances: !openSections.allowances })}
                >
                    <table className="w-full text-[13px]">
                        <tbody>
                            <tr><td className="py-1 text-slate-600">Sub Total <span className="text-[11px] text-slate-400">(Net Matl + Conv + Cut + Weld)</span></td><td className="text-right font-mono tabular-nums font-semibold" data-testid="zbc-sub-total">{fmtINR(z.sub_total ?? z.base_cost ?? 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">ICC <span className="text-[11px] text-slate-400">(1.0% on Input RM Cost)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-icc">{fmtINR(z.icc_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">Tool Maintenance <span className="text-[11px] text-slate-400">(1.0% on Conv. Cost)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-tool-maint">{fmtINR(z.tool_maint_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">Rejection <span className="text-[11px] text-slate-400">(0.25% on Sub Total)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-rejection">{fmtINR(z.rejection_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">P&amp;F <span className="text-[11px] text-slate-400">(0.4% on Sub Total)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-pf">{fmtINR(z.pf_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">Profit <span className="text-[11px] text-slate-400">(3.0% on Sub Total)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-profit">{fmtINR(z.profit_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">Overhead <span className="text-[11px] text-slate-400">(2.0% on Sub Total)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-oh">{fmtINR(z.oh_amount || 0)}</td></tr>
                            <tr><td className="py-1 text-slate-600">TPT <span className="text-[11px] text-slate-400">(₹0.50 × Input Wt)</span></td><td className="text-right font-mono tabular-nums" data-testid="zbc-tpt">{fmtINR(z.tpt_amount || 0)}</td></tr>
                            <tr className="border-t border-slate-200"><td className="py-2 font-semibold">Total Allowances</td><td className="text-right font-mono tabular-nums font-semibold">{fmtINR(z.allowance_total)}</td></tr>
                        </tbody>
                    </table>
                </Accordion>

                <div className="px-5 py-4 border-t border-slate-200 bg-[hsl(var(--copper-soft))] flex items-baseline justify-between">
                    <span className="font-display text-lg font-semibold">SHOULD-COST</span>
                    <span className="font-display text-2xl font-bold text-[hsl(var(--copper))] font-mono tabular-nums">{fmtINR(z.should_cost)}</span>
                </div>
            </Card>

            {/* Recalc history if any */}
            {(z.recalc_history || []).length > 0 && (
                <Card className="mt-4 p-5">
                    <div className="eyebrow text-slate-500 mb-3">RECALCULATION HISTORY</div>
                    <div className="space-y-2">
                        {z.recalc_history.map((h, i) => (
                            <div key={i} className="rounded-md border border-slate-200 p-3 text-[12px]">
                                <div className="flex items-start justify-between mb-1">
                                    <Pill tone={h.decision === "CONFIRMED" ? "emerald" : "amber"}>{h.decision === "CONFIRMED" ? "Recalculated" : "Kept old rate"}</Pill>
                                    <span className="text-slate-400">{fmtDate(h.at)} · {h.by_name}</span>
                                </div>
                                {h.decision === "CONFIRMED" && (
                                    <div className="font-mono tabular-nums">
                                        Rate ₹{h.old_rate} → ₹{h.new_rate} · Should-Cost {fmtINR(h.old_should_cost)} → {fmtINR(h.new_should_cost)}
                                        <span className={`ml-2 ${h.impact >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                            ({h.impact >= 0 ? "+" : ""}{fmtINR(h.impact)} · {h.impact_pct >= 0 ? "+" : ""}{h.impact_pct}%)
                                        </span>
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </Card>
            )}

            {/* Supplier ZBC Comparison: captured at save time from the AI wizard.
                Renders only when the ZBC was saved with a supplier sheet attached. */}
            {z.supplier_zbc && z.supplier_comparison && (
                <SavedSupplierComparison
                    supplier={z.supplier_zbc}
                    comparison={z.supplier_comparison}
                    ourShouldCost={z.should_cost}
                />
            )}

            {/* Audit Log */}
            <div className="mt-4">
                <AuditTrail entityType="ZBC" entityId={z.id} />
            </div>

            <VarianceBreakdownModal
                open={breakdownOpen}
                onClose={() => setBreakdownOpen(false)}
                loading={breakdownLoading}
                data={breakdown}
            />

            <RateImpactModal
                open={impactOpen}
                onClose={() => setImpactOpen(false)}
                loading={impactLoading}
                data={impact}
                reason={z.recalc_reason}
                onConfirm={confirmRecalc}
                onKeep={keepOldRate}
                submitting={recalcSubmitting}
            />

            <WhatIfModal
                open={whatIfOpen}
                onClose={() => setWhatIfOpen(false)}
                form={whatIfForm}
                onChange={(patch) => setWhatIfForm({ ...whatIfForm, ...patch })}
                loading={whatIfLoading}
                result={whatIfResult}
            />
        </div>
    );
}

function VarianceBreakdownModal({ open, onClose, loading, data }) {
    return (
        <Modal open={open} onClose={onClose} title="Variance Breakup" size="lg">
            {loading && <LoadingState />}
            {!loading && data && !data.available && (
                <div className="text-[13px] text-slate-600 leading-relaxed">
                    <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                        {data.reason}
                    </div>
                </div>
            )}
            {!loading && data && data.available && (
                <div className="space-y-4">
                    <p className="text-[12.5px] text-slate-600">
                        Decomposes the gap between our should-cost and the supplier's net price into three drivers:
                        weight/geometry, steel rate, and everything else (process, allowances, profit stack).
                    </p>

                    <div className="rounded-md border border-slate-200 overflow-hidden">
                        <div className="grid grid-cols-3 divide-x divide-slate-200 bg-slate-50">
                            <div className="px-4 py-3">
                                <div className="eyebrow text-slate-500">SHOULD-COST (OURS)</div>
                                <div className="font-mono tabular-nums text-base font-semibold">{fmtINR(data.should_cost_ours)}</div>
                            </div>
                            <div className="px-4 py-3">
                                <div className="eyebrow text-slate-500">SUPPLIER NET PRICE</div>
                                <div className="font-mono tabular-nums text-base font-semibold">{fmtINR(data.net_price_supplier)}</div>
                            </div>
                            <div className="px-4 py-3">
                                <div className="eyebrow text-slate-500">TOTAL GAP</div>
                                <div className={`font-mono tabular-nums text-base font-semibold ${data.total_gap >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                    {data.total_gap >= 0 ? "+" : ""}{fmtINR(data.total_gap)}
                                </div>
                            </div>
                        </div>
                    </div>

                    <BreakdownRow
                        label="Weight / Geometry"
                        value={data.weight_driven}
                        note={data.weight_driven == null ? "Supplier didn't report a usable weight" : "Their claimed weight vs. our independently-derived weight, at our rate"}
                        total={data.total_gap}
                    />
                    <BreakdownRow
                        label="Steel Rate"
                        value={data.steel_rate_driven}
                        note={
                            data.steel_rate_driven == null
                                ? "Supplier didn't report their assumed RM rate"
                                : `Our rate ₹${data.our_rm_rate}/kg vs. their assumed ₹${data.supplier_rm_rate}/kg`
                        }
                        total={data.total_gap}
                    />
                    <BreakdownRow
                        label="Process & Other"
                        value={data.process_driven}
                        note="Conversion cost, allowances, and commercial-stack differences: whatever's left after weight and rate are accounted for"
                        total={data.total_gap}
                    />

                    {data.note && (
                        <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-[12px] text-amber-900 flex items-start gap-2">
                            <AlertTriangle className="h-3.5 w-3.5 mt-0.5 shrink-0 text-amber-600" />
                            {data.note}
                        </div>
                    )}
                </div>
            )}
        </Modal>
    );
}

function RateImpactModal({ open, onClose, loading, data, reason, onConfirm, onKeep, submitting }) {
    return (
        <Modal open={open} onClose={onClose} title="Price Impact · Steel Rate Change" size="lg">
            {loading && <LoadingState />}
            {!loading && data && (
                <div className="space-y-4">
                    <p className="text-[12.5px] text-slate-600">
                        {reason || "A steel grade rate has changed since this ZBC was last calculated."} Nothing is
                        changed automatically: review the impact below, then confirm the recalculation or keep the
                        old rate for now.
                    </p>

                    <div className="rounded-md border border-slate-200 overflow-hidden">
                        <div className="grid grid-cols-2 divide-x divide-slate-200">
                            <div className="px-4 py-3 bg-slate-50">
                                <div className="eyebrow text-slate-500">CURRENT (STORED)</div>
                                <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(data.old_should_cost)}</div>
                                <div className="text-[11px] text-slate-500 mt-0.5">at {fmtINR(data.old_rate)}/kg</div>
                            </div>
                            <div className="px-4 py-3 bg-[hsl(var(--copper-soft))]">
                                <div className="eyebrow text-slate-600">IF RECALCULATED NOW</div>
                                <div className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(data.new_should_cost)}</div>
                                <div className="text-[11px] text-slate-600 mt-0.5">at {fmtINR(data.new_rate)}/kg</div>
                            </div>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-200">
                            <div className="eyebrow text-slate-500">IMPACT</div>
                            <div className={`font-mono tabular-nums text-base font-semibold ${data.impact >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                {data.impact >= 0 ? "+" : ""}{fmtINR(data.impact)}
                                <span className="text-[11px] font-normal ml-1">({data.impact_pct >= 0 ? "+" : ""}{data.impact_pct}%)</span>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end gap-2">
                        <SlateButton variant="outline" onClick={onKeep} disabled={submitting}>Keep Old Rate</SlateButton>
                        <CopperButton onClick={onConfirm} disabled={submitting}>
                            {submitting ? "Recalculating…" : "Confirm Recalculation"}
                        </CopperButton>
                    </div>
                </div>
            )}
        </Modal>
    );
}

function WhatIfModal({ open, onClose, form, onChange, loading, result }) {
    return (
        <Modal open={open} onClose={onClose} title="What-If · Try a Hypothetical Rate" size="lg">
            <div className="space-y-4">
                <p className="text-[12.5px] text-slate-600">
                    A sandbox to explore — nothing here is saved. Try a different RM rate, scrap rate, or an
                    overall conversion cost change, and see the effect on should-cost immediately.
                </p>

                <div className="grid grid-cols-3 gap-3">
                    <Field label="RM Rate (₹/kg)">
                        <NumberInput
                            value={form.rm_rate}
                            onChange={(e) => onChange({ rm_rate: e.target.value })}
                            step="0.01"
                            min="0"
                            data-testid="whatif-rm-rate"
                        />
                    </Field>
                    <Field label="Scrap Rate (₹/kg)">
                        <NumberInput
                            value={form.scrap_rate}
                            onChange={(e) => onChange({ scrap_rate: e.target.value })}
                            step="0.01"
                            min="0"
                            data-testid="whatif-scrap-rate"
                        />
                    </Field>
                    <Field label="Conversion Cost Δ%" helper="Applies to every operation, cutting and welding rate">
                        <NumberInput
                            value={form.conversion_cost_delta_pct}
                            onChange={(e) => onChange({ conversion_cost_delta_pct: e.target.value })}
                            step="1"
                            data-testid="whatif-conversion-delta"
                        />
                    </Field>
                </div>

                {loading && <LoadingState />}
                {!loading && result && (
                    <div className="rounded-md border border-slate-200 overflow-hidden">
                        <div className="grid grid-cols-2 divide-x divide-slate-200">
                            <div className="px-4 py-3 bg-slate-50">
                                <div className="eyebrow text-slate-500">CURRENT (STORED)</div>
                                <div className="font-mono tabular-nums text-lg font-semibold">{fmtINR(result.old_should_cost)}</div>
                            </div>
                            <div className="px-4 py-3 bg-[hsl(var(--copper-soft))]">
                                <div className="eyebrow text-slate-600">UNDER THIS WHAT-IF</div>
                                <div className="font-mono tabular-nums text-lg font-semibold text-[hsl(var(--copper))]">{fmtINR(result.new_should_cost)}</div>
                            </div>
                        </div>
                        <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-between">
                            <div>
                                <div className="eyebrow text-slate-500">IMPACT</div>
                                <div className={`font-mono tabular-nums text-base font-semibold ${result.impact >= 0 ? "text-red-600" : "text-emerald-600"}`}>
                                    {result.impact >= 0 ? "+" : ""}{fmtINR(result.impact)}
                                    <span className="text-[11px] font-normal ml-1">({result.impact_pct >= 0 ? "+" : ""}{result.impact_pct}%)</span>
                                </div>
                            </div>
                            <div className="text-right text-[11px] text-slate-500">
                                <div>Material {fmtINR(result.net_material_cost)}</div>
                                <div>Conversion {fmtINR(result.conversion_cost)} · Cutting {fmtINR(result.cutting_cost)} · Welding {fmtINR(result.welding_cost)}</div>
                            </div>
                        </div>
                    </div>
                )}

                <div className="flex justify-end">
                    <SlateButton variant="outline" onClick={onClose}>Close</SlateButton>
                </div>
            </div>
        </Modal>
    );
}

function BreakdownRow({ label, value, note, total }) {
    const pct = value != null && total ? Math.round((value / total) * 100) : null;
    return (
        <div className="rounded-md border border-slate-200 p-3">
            <div className="flex items-center justify-between">
                <div className="text-[13px] font-semibold text-slate-800">{label}</div>
                <div className={`font-mono tabular-nums text-[15px] font-semibold ${
                    value == null ? "text-slate-400" : value > 0 ? "text-red-600" : value < 0 ? "text-emerald-600" : "text-slate-700"
                }`}>
                    {value == null ? "—" : `${value >= 0 ? "+" : ""}${fmtINR(value)}`}
                    {pct != null && <span className="text-[10.5px] font-normal text-slate-400 ml-1">({pct}%)</span>}
                </div>
            </div>
            <div className="text-[11px] text-slate-500 mt-1">{note}</div>
        </div>
    );
}

function SavedSupplierComparison({ supplier, comparison, ourShouldCost }) {
    const rows = comparison?.rows || [];
    const fi = comparison?.financial_impact || {};
    const sourceLabel =
        supplier?.source === "UPLOAD"
            ? `${supplier.filename || "workbook"} · sheet "${supplier.sheet_name || "?"}"`
            : `Manual entry${supplier?.supplier_name ? ` · ${supplier.supplier_name}` : ""}`;
    const impactPositive = (fi.delta ?? 0) > 0;
    return (
        <Card className="mt-4" data-testid="saved-supplier-comparison">
            <div className="p-4 border-b border-slate-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                    <FileSpreadsheet className="w-4 h-4 text-[hsl(var(--copper))]" />
                    <div className="text-[13px] font-semibold text-slate-800">Supplier ZBC Comparison</div>
                    <Pill tone="emerald" className="text-[10px]">
                        {supplier?.source === "MANUAL" ? "Manual entry" : "From workbook"}
                    </Pill>
                </div>
                <div className="text-[11px] text-slate-500">
                    Captured {fmtDate(supplier?.captured_at_save || supplier?.uploaded_at)}
                </div>
            </div>
            <div className="px-4 py-3 border-b border-slate-100 bg-slate-50/60 flex items-center justify-between text-[12px]">
                <div className="text-slate-600">
                    <span className="font-semibold text-slate-800">Source:</span> {sourceLabel}
                </div>
                {fi.delta != null && (
                    <div className="flex items-center gap-4">
                        <div>
                            <span className="text-slate-500">Ours: </span>
                            <span className="font-mono font-semibold">{fmtINR(fi.ours_should_cost || ourShouldCost || 0, 2)}</span>
                        </div>
                        <div>
                            <span className="text-slate-500">Supplier: </span>
                            <span className="font-mono font-semibold">{fmtINR(fi.supplier_net_price || 0, 2)}</span>
                        </div>
                        <div className={`inline-flex items-center gap-1 font-mono font-semibold ${impactPositive ? "text-red-600" : "text-emerald-600"}`}>
                            {impactPositive ? <TrendingUp className="w-3.5 h-3.5" /> : <TrendingDown className="w-3.5 h-3.5" />}
                            {fi.delta >= 0 ? "+" : ""}{fmtINR(fi.delta, 2)}
                            <span className="text-[10.5px] opacity-75">({fi.delta_pct >= 0 ? "+" : ""}{fi.delta_pct}%)</span>
                        </div>
                        {fi.annual_saving_potential != null && (
                            <div className={`text-[11px] ${impactPositive ? "text-red-600" : "text-emerald-600"}`}>
                                Annual: {fi.annual_saving_potential >= 0 ? "+" : ""}{fmtINR(fi.annual_saving_potential)}
                            </div>
                        )}
                    </div>
                )}
            </div>
            <div className="overflow-x-auto">
                <table className="w-full text-[12.5px]">
                    <thead className="bg-slate-50/60 border-b border-slate-200">
                        <tr>
                            <th className="text-left py-2 px-3 eyebrow text-slate-500">Metric</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">CostIQ (Ours)</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Supplier</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Delta</th>
                            <th className="text-right py-2 px-3 eyebrow text-slate-500">Δ %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((r) => {
                            const isNetPrice = r.key === "net_price";
                            const sevColor =
                                !r.severity ? "text-slate-400" :
                                r.severity === "INFO" ? "text-slate-700" :
                                r.severity === "WARN" ? "text-amber-700" :
                                "text-red-700";
                            return (
                                <tr key={r.key} className={`border-b border-slate-100 ${isNetPrice ? "bg-amber-50/50 font-semibold" : ""}`}>
                                    <td className="py-1.5 px-3">{r.label}</td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">
                                        {r.ours != null ? fmtINR(r.ours, 2) : "—"}
                                    </td>
                                    <td className="py-1.5 px-3 text-right font-mono tabular-nums">
                                        {r.supplier != null ? fmtINR(r.supplier, 2) : "—"}
                                    </td>
                                    <td className={`py-1.5 px-3 text-right font-mono tabular-nums ${sevColor}`}>
                                        {r.delta != null ? (r.delta >= 0 ? "+" : "") + fmtINR(r.delta, 2) : "—"}
                                    </td>
                                    <td className={`py-1.5 px-3 text-right font-mono tabular-nums ${sevColor}`}>
                                        {r.delta_pct != null ? `${r.delta_pct >= 0 ? "+" : ""}${r.delta_pct}%` : "—"}
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            <div className="px-4 py-2 text-[10.5px] text-slate-500 bg-slate-50/40">
                Positive delta = supplier is <span className="text-red-600 font-semibold">above</span> CostIQ&#39;s should-cost.
                Amber &gt; 5%, red &gt; 15%.
            </div>
        </Card>
    );
}

function SummaryCard({ label, value, hint, badge, tone, onClick }) {
    return (
        <Card
            hover={!!onClick}
            onClick={onClick}
            className={`p-4 ${tone === "copper" ? "border-[hsl(var(--copper))]/30 bg-[hsl(var(--copper-soft))]" : ""}`}
        >
            <div className="eyebrow text-slate-500 flex items-center justify-between">
                {label}
                {onClick && <span className="text-[9.5px] normal-case tracking-normal text-slate-400">Click for breakdown</span>}
            </div>
            <div className={`mt-1 font-mono tabular-nums text-xl font-semibold ${tone === "copper" ? "text-[hsl(var(--copper))]" : "text-slate-900"}`}>
                {value}
            </div>
            {badge && <div className="mt-2"><VarianceBadge badge={badge} showPct={false} /></div>}
            {hint && <div className="mt-1 text-[11px] font-mono tabular-nums text-slate-500">{hint}</div>}
        </Card>
    );
}

function Accordion({ title, icon: Icon, value, open, onToggle, children }) {
    return (
        <div className="border-b border-slate-100 last:border-0">
            <button onClick={onToggle} className="w-full px-5 py-3 flex items-center justify-between hover:bg-slate-50 text-left">
                <div className="flex items-center gap-2.5">
                    {open ? <ChevronDown className="h-3.5 w-3.5 text-slate-400" /> : <ChevronRight className="h-3.5 w-3.5 text-slate-400" />}
                    {Icon && <Icon className="h-4 w-4 text-slate-500" />}
                    <span className="text-sm font-medium">{title}</span>
                </div>
                <span className="font-mono tabular-nums text-sm font-semibold">{value != null ? `₹${(value || 0).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : "—"}</span>
            </button>
            {open && <div className="px-5 pb-4 pt-1">{children}</div>}
        </div>
    );
}

function quoteAge(d) {
    const days = Math.floor((Date.now() - new Date(d).getTime()) / (1000 * 60 * 60 * 24));
    if (days > 180) return `${days} days · STALE`;
    return `${days} days old`;
}
